import React from 'react'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsTooltip } from './HlsTooltip'

/** Share icon — Bitmovin share.svg (24x19 viewBox, forward-pointing arrow). */
const ShareIcon: React.FC = () => (
  <svg width={24} height={19} viewBox="0 0 24 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#fff"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.001 17.625c.193-2.004.585-4.003 1.356-5.87a12.6 12.6 0 012.487-3.964 11.3 11.3 0 013.121-2.35A12.4 12.4 0 0110.254 4.296c.966-.2 1.955-.311 2.941-.333l-.001-3.535c0-.294.367-.557.633-.364l.991.723c.794.58 1.586 1.156 2.381 1.737.96.7 1.918 1.4 2.88 2.101.828.605 1.66 1.21 2.489 1.816.403.293.809.585 1.212.885l.017.011c.269.196.269.529 0 .726l-.992.723c-.793.58-1.585 1.156-2.38 1.737-.961.7-1.919 1.4-2.88 2.101-.829.605-1.66 1.21-2.49 1.815-.401.294-.795.614-1.212.883l-.017.013c-.266.193-.633-.069-.633-.363v-3.524c-1.04.011-2.082.064-3.113.196l-.036.001h-.006l-.005.001h-.012l-.01.001c-.018.002-.036.005-.038.005a7 7 0 00-.18.025 11 11 0 00-.386.062c-.25.042-.499.086-.746.143-.46.103-.914.226-1.358.379a12 12 0 00-.65.243l-.058.023-.03.002.062-.009.01.007c-.044.02-.091.039-.137.062a9 9 0 00-.308.142c-.395.19-.778.406-1.145.648a8 8 0 00-.554.386l-.124.092.012-.011-.036.028.01-.006a7 7 0 00-.045.04c-.098.078-.196.157-.29.237a11 11 0 00-1.015.952c-.386.401-.75.824-1.096 1.26l-.134.17-.002.003.002-.003-.022.028.009-.011-.011.014a8 8 0 00-.068.068 13 13 0 00-.269.364c-.192.264-.38.532-.562.801a.42.42 0 01-.473.193.39.39 0 01-.317-.393z"
    />
  </svg>
)

interface HlsShareButtonProps {
  onShare: () => void
}

/**
 * Content Share button for the HLS.js player control bar.
 * Pauses playback, exits fullscreen, and triggers the share modal
 * (owned by a parent component like CollectiblesPlayer).
 */
export const HlsShareButton: React.FC<HlsShareButtonProps> = ({ onShare }) => {
  const track = useSafeTrack()
  const { controller } = useHlsPlayerUI()
  const { t } = useTranslate('common')

  const handleClick = () => {
    controller?.exitFullscreen()
    controller?.pause()
    onShare()
    track('Clicked Guild Content Share Bitmovin Player CTA Button')
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/20"
      aria-label={t('shareFreeAccess', 'Share Free Access')}
    >
      <ShareIcon />
      <HlsTooltip
        title={t('shareFreeAccess', 'Share Free Access')}
        subtitle={t('shareFreeAccessDescription', 'Share this goodness with a friend.')}
      />
    </button>
  )
}
